import { DeleteConfirmationPopover } from '@/components/DeleteConfirmationPopover';
import { HighlightableText } from '@/components/HighlightableText';
import { HoverDisplay } from '@/components/HoverDisplay';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { RestrictedContentManager } from '@/pages/PatientTimelinePage/RestrictedContentManager';
import { changeResourceConfidentiality } from '@/services/permissions';
import { ConfidentialityLevel } from '@bariendo/lib/constants';
import { ActionIcon, Box, Flex, Group, Menu, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { formatHumanName, getReferenceString } from '@medplum/core';
import { Communication, Reference, Resource } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  IconArrowUpRight,
  IconDotsVertical,
  IconEdit,
  IconLink,
  IconLock,
  IconNote,
  IconTrash,
} from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { TimelineNote } from '@/components/Timeline/types';
import { DateTimeTooltip } from '@/components/Timeline/TimelineItems/DateTimeTooltip';

interface TimelineNoteDisplayProps {
  note: TimelineNote;
  onEdit: (note: Communication) => void;
  onDelete: (noteReference: Reference<Communication>) => Promise<void>;
  copyDirectLink: (itemReference: string) => Promise<void>;
  isDeletingNote: boolean;
  hasActiveFilters: boolean;
  handleNavigateToItem: (itemReference: string) => void;
  searchTerm?: string;
}

export function TimelineNoteDisplay({
  note,
  onEdit,
  onDelete,
  copyDirectLink,
  isDeletingNote,
  hasActiveFilters,
  handleNavigateToItem,
  searchTerm,
}: TimelineNoteDisplayProps) {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const {
    currentUserPermissions: { canEditNote, canAccessRestrictedContent },
  } = useCurrentUser();
  const medplum = useMedplum();

  const changeResourceRestriction = useCallback(
    async (resourceRef: Reference<Resource>, confidentialityLevel: ConfidentialityLevel) => {
      await changeResourceConfidentiality(medplum, {
        resourceRef,
        confidentialityLevel,
      });
    },
    [medplum]
  );

  const canEdit = useMemo(() => {
    return canEditNote(note.resource);
  }, [canEditNote, note.resource]);

  const handleEditNote = () => {
    onEdit(note.resource);
  };

  const handleDeleteNote = async () => {
    await onDelete(note.reference);
    setShowDeleteConfirmation(false);
  };

  const isRestricted = note.confidentiality === ConfidentialityLevel.RESTRICTED;
  const isParentRestricted = note.parentConfidentiality === ConfidentialityLevel.RESTRICTED;

  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      bg="#fffbeb"
      style={{
        borderColor: '#FCECCC',
      }}
    >
      <HoverDisplay>
        <Stack gap="0" w="100%">
          <Flex justify="space-between">
            <Group justify="flex-start" m={0} gap="8">
              <IconNote color="#C6A467" size={16} />
              <Text size="xs" c="#C6A467">
                Sticky note
              </Text>
            </Group>
            <Flex gap="xs" align="center" justify="center">
              {/* Kebab menu */}
              <HoverDisplay.Target>
                <DeleteConfirmationPopover
                  onConfirm={handleDeleteNote}
                  isLoading={isDeletingNote}
                  title="Delete this note?"
                  description="This can't be undone."
                  opened={showDeleteConfirmation}
                  onClose={() => setShowDeleteConfirmation(false)}
                >
                  <Menu shadow="md" width={200} position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray" size="sm" style={{ flexShrink: 0 }}>
                        <IconDotsVertical size={24} />
                      </ActionIcon>
                    </Menu.Target>

                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconLink size={18} color="#246A7A" />}
                        onClick={() => copyDirectLink(getReferenceString(note.reference)!)}
                      >
                        Copy link
                      </Menu.Item>

                      {!isRestricted && canAccessRestrictedContent && (
                        <Menu.Item
                          leftSection={<IconLock size={18} color="#246A7A" />}
                          onClick={() => changeResourceRestriction(note.reference, ConfidentialityLevel.RESTRICTED)}
                        >
                          Mark as sensitive
                        </Menu.Item>
                      )}
                      {isRestricted && !isParentRestricted && canAccessRestrictedContent && (
                        <Menu.Item
                          leftSection={<IconLock size={18} color="#246A7A" />}
                          onClick={() => changeResourceRestriction(note.reference, ConfidentialityLevel.NORMAL)}
                        >
                          Mark as not sensitive
                        </Menu.Item>
                      )}
                      {canEdit && (
                        <Menu.Item leftSection={<IconEdit size={18} color="#246A7A" />} onClick={handleEditNote}>
                          <Text>Edit</Text>
                        </Menu.Item>
                      )}
                      {canEdit && (
                        <Menu.Item
                          leftSection={<IconTrash size={18} color="var(--mantine-color-red-5)" />}
                          onClick={() => setShowDeleteConfirmation(true)}
                        >
                          <Text c="red.6">Delete</Text>
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </DeleteConfirmationPopover>
              </HoverDisplay.Target>

              {hasActiveFilters && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => handleNavigateToItem(getReferenceString(note.reference)!)}
                  title="See full context"
                >
                  <IconArrowUpRight size={16} />
                </ActionIcon>
              )}
            </Flex>
          </Flex>
          <Group gap="xxs">
            <Text>
              {note.sender && (
                <>
                  <Text component="span" variant="section" pr="xs">
                    {note.sender.resourceType === 'Organization'
                      ? (note.sender as { name: string }).name
                      : formatHumanName(note.sender.name?.[0])}
                  </Text>
                </>
              )}
            </Text>
            <DateTimeTooltip date={note.timestamp} />

            {isRestricted && (
              <Tooltip label="This note contains sensitive information">
                <IconLock size={18} />
              </Tooltip>
            )}
          </Group>
          <Group justify="space-between" align="flex-start" mt="xs">
            <Box style={{ flex: 1 }}>
              <RestrictedContentManager isRestricted={note.hiddenPayload}>
                <HighlightableText
                  text={note.content || ''}
                  richTextContent={note.richTextContent}
                  searchTerm={searchTerm || ''}
                  size="md"
                  style={{ whiteSpace: 'pre-wrap' }}
                />
              </RestrictedContentManager>
            </Box>
          </Group>
          {note.isEdited && (
            <Text size="xxs" c="dimmed">
              (edited)
            </Text>
          )}
        </Stack>
      </HoverDisplay>
    </Paper>
  );
}
